import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { PasswordModule } from 'primeng/password';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { environment } from '../../../../environments/environments';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    ButtonModule, 
    CardModule, 
    PasswordModule, 
    ToastModule
  ],
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  providers: [MessageService]
})
export class ResetPasswordComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);
  private messageService = inject(MessageService);
  isMobile = window.innerWidth < 768;

  token = '';
  newPassword = '';
  confirmPassword = '';
  isLoading = false;
  isSuccess = false;
  errorMessage = '';
  successMessage = '';

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.token = params['token'];
      if (!this.token) {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Token de réinitialisation invalide',
          life: 5000
        });
      }
    });
  }

  onResetPassword() {
    if (!this.isFormValid()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attention',
        detail: 'Veuillez corriger les erreurs du formulaire',
        life: 3000
      });
      return;
    }

    this.isLoading = true;

    const apiUrl = environment.apiUrl + 'api/auth/reset-password';

    this.http.post(apiUrl, {
      token: this.token,
      new_password: this.newPassword
    }).pipe(
      catchError(error => {
        console.error('Reset password error:', error);
        return of({ 
          error: error.error?.error || 'Failed to reset password' 
        });
      })
    ).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        
        if (response.error) {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: response.error,
            life: 5000
          });
        } else {
          this.isSuccess = true;
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'Mot de passe réinitialisé avec succès !',
            life: 5000
          });
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur réseau',
          detail: 'Erreur réseau. Veuillez réessayer.',
          life: 5000
        });
      }
    });
  }

  goToLogin() {
    this.router.navigate(['/auth/login']);
  }

  isFormValid(): boolean {
    return this.newPassword.length >= 8 && 
           this.newPassword === this.confirmPassword;
  }

  getPasswordStrength(): number {
    let score = 0;
    if (this.newPassword.length >= 8) score++;
    if (this.hasUpperCase(this.newPassword)) score++;
    if (this.hasLowerCase(this.newPassword)) score++;
    if (this.hasNumber(this.newPassword)) score++;
    if (this.hasSpecialChar(this.newPassword)) score++;
    return score;
  }




  getPasswordStrengthText(): string {
    const strength = this.getPasswordStrength();
    if (strength <= 2) return 'Faible';
    if (strength <= 3) return 'Moyen';
    if (strength <= 4) return 'Bon';
    return 'Excellent';
  }

  hasUpperCase(str: string): boolean {
    return /[A-Z]/.test(str);
  }

  hasLowerCase(str: string): boolean {
    return /[a-z]/.test(str);
  }

  hasNumber(str: string): boolean {
    return /\d/.test(str);
  }

  hasSpecialChar(str: string): boolean {
    return /[!@#$%^&*(),.?":{}|<>]/.test(str);
  }
}